/* Ajouter une destination — DS-02 (Bible §18).
   Batch 9: the platform list is the seam’s own — `PLATFORMS` (six), each with
   its lettermark. Pasting a link runs the seven technical checks and writes the
   SAME ref the campaign stepper and « Liens d’avis » write (`addDestination`),
   so a link added here is a link added everywhere. The candour line is the
   point: a technical pass is never a compliance verdict.
   « Lien personnalisé » continues to DS-08 (two checks), « Facebook » to DS-09. */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Note, PlatformMark, useToast } from '@/ui'
import { PLATFORMS, addDestination, getDestinations, platformLabel, type PlatformId } from '@/lib/campaign'
import { useLocations } from '@/lib/api'
import { ALL, useScope } from '@/lib/scope'
import { CANDEUR, CONTROLES, Retour } from './parts'

export default function Ajouter() {
  const [platform, setPlatform] = useState<PlatformId>('google')
  const [url, setUrl] = useState('')
  const nav = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [scope] = useScope()
  const { data: locations } = useLocations()

  const lieux = (locations ?? []).filter(l => l.status !== 'inactive')
  const lieu = (scope !== ALL ? lieux.find(l => l.id === scope) : undefined) ?? lieux[0]
  const locId = lieu?.id ?? 'l0'
  const locName = lieu?.name ?? 'votre établissement'
  /* one platform per establishment — a second paste replaces the link rather
     than adding a row, and the merchant should read that before he pastes */
  const deja = new Set(getDestinations().filter(d => d.locationId === locId).map(d => d.platform))

  const valider = () => {
    if (platform === 'custom') { nav('/destinations/lien'); return }
    if (platform === 'facebook') { nav('/destinations/facebook'); return }
    if (!url.trim()) return
    addDestination(locId, platform, locName, { url: url.trim() })
    qc.invalidateQueries({ queryKey: ['destinations'] })
    toast({ message: `✓ ${platformLabel(platform)} — lien vérifié pour ${locName}.` })
    nav('/destinations')
  }

  const suivant = platform === 'custom' || platform === 'facebook'

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 620 }}>
      <Retour />
      <h2 className="t-page" style={{ margin: 0 }}>Ajouter un lien d’avis</h2>
      <p className="f-help" style={{ margin: 0 }}>
        Une plateforme par établissement, autant de liens à vous que vous voulez.
        Nous vérifions que le lien s’ouvre — dix secondes.
      </p>

      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
        {PLATFORMS.map(p => (
          <Button key={p.id} variant={platform === p.id ? 'primary' : 'quiet'}
            onClick={() => setPlatform(p.id)}>
            <PlatformMark platform={p.id} size={18} /> {p.label}
          </Button>
        ))}
      </div>

      {!suivant && (
        <Field label={`Collez le lien de votre fiche ${platformLabel(platform)}`}
          help={deja.has(platform)
            ? `${platformLabel(platform)} est déjà liée à ${locName} — un nouveau lien remplace l’ancien.`
            : 'Le lien public où vos clients laissent leur avis.'}>
          {id => <Input id={id} value={url} onChange={e => setUrl(e.currentTarget.value)}
            placeholder={`https://…${platform}…`} />}
        </Field>
      )}

      <Note tone="ok">✓ Vérifications&nbsp;: {CONTROLES.join(' · ')}.</Note>

      <Note tone="candour">{CANDEUR}</Note>

      <div><Button variant="primary" disabled={!suivant && !url.trim()} onClick={valider}>
        {suivant ? 'Continuer' : 'Valider et enregistrer'}
      </Button></div>
    </div>
  )
}
